/**
 * UnifiedDashboardNudge — bidirectional nudge card that closes the gap
 * between the two entry paths into the platform:
 *
 *   variant="tools"  → Shown to buyers who completed the quiz but haven't
 *                       purchased any tools. Nudges them to explore the
 *                       Buyer Toolkit.
 *   variant="quiz"   → Shown to buyers who purchased a tool but haven't
 *                       completed the personalized RV match quiz yet.
 *                       Nudges them to take the free quiz.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, ShoppingBag, ClipboardCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = (variant) => `rvmatch:nudge:${variant}:dismissed`;

const UnifiedDashboardNudge = ({ variant, email, sessionId }) => {
  const [purchases, setPurchases] = useState(null); // null = loading
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY(variant)) === '1'); } catch { /* ignore */ }
  }, [variant]);

  useEffect(() => {
    const target = (email || '').trim().toLowerCase();
    if (!target || !target.includes('@')) { setPurchases([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/purchases`, { params: { email: target } });
        if (!cancelled) setPurchases(res.data?.products || []);
      } catch {
        if (!cancelled) setPurchases([]);
      }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const shouldShow = useMemo(() => {
    if (dismissed || purchases === null) return false;
    if (variant === 'tools') return purchases.length === 0;
    if (variant === 'quiz')  return purchases.length > 0 && !sessionId;
    return false;
  }, [variant, purchases, sessionId, dismissed]);

  if (!shouldShow) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY(variant), '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (variant === 'tools') {
    return (
      <div
        className="relative overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-sky-500/8 to-transparent p-5"
        data-testid="nudge-tools"
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300 mb-1">Level up your search</p>
            <h3 className="text-base font-black text-white mb-1">You matched — now let&apos;s help you close the deal.</h3>
            <p className="text-sm text-slate-300 mb-3">
              Buyers who use our Deal Analyzer + Negotiation Coach save an average of <strong>$2,800</strong> at the dealership. Grab the Buyer Toolkit for one price — all 4 tools, lifetime access.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/tools-bundle"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#05070d] text-sm font-black"
                data-testid="nudge-tools-cta"
              >
                <Sparkles className="w-3.5 h-3.5" /> Explore the Buyer Toolkit <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <button
                type="button"
                onClick={dismiss}
                className="text-xs text-slate-400 hover:text-white"
                data-testid="nudge-tools-dismiss"
              >
                Not right now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // variant === 'quiz'
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-orange-500/8 to-transparent p-5"
      data-testid="nudge-quiz"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0">
          <ClipboardCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-1">Get personal matches</p>
          <h3 className="text-base font-black text-white mb-1">Your tool is way sharper with a match profile.</h3>
          <p className="text-sm text-slate-300 mb-3">
            Complete the free 2-minute RV Match form — the results feed straight into your tools so every calculation and negotiation script is tailored to <strong>your</strong> RV type, budget, and tow rig.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#05070d] text-sm font-black"
              data-testid="nudge-quiz-cta"
            >
              <Sparkles className="w-3.5 h-3.5" /> Start free RV Match <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              type="button"
              onClick={dismiss}
              className="text-xs text-slate-400 hover:text-white"
              data-testid="nudge-quiz-dismiss"
            >
              Not right now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboardNudge;
